/**
 * Admin console shell + Creative Review Board — port of the monolith's #admin
 * section (index.html L1293–1320) and its board engine (L5842–5890).
 *
 *   - adm-tabs bar: Review Board · Review Queue · Back-end detail · Pipeline ·
 *     Audiences & DEs · Diagnostics. (The monolith's #mvpSwitch is gone —
 *     MVP-only cutover 2026-08-09, no mode to switch to.)
 *   - Board columns = the SIX REAL production stages (ABX_STAGES); legacy
 *     status names normalize through abxStage()/ABX_RS_FROM_REAL.
 *   - Data = /mirror-jobs (Projects Mirror — session-independent), mapped the
 *     way admMirrorLoad maps rows.
 *   - The full review modal (admExpand: Revise in Studio, gate, lock, approve,
 *     schedule) is an L-size follow-up; the simpler detail modal keeps the
 *     /mirror-status stage actions in the meantime. The five other console
 *     tabs' content is likewise unported — they say so honestly.
 */
import { useCallback, useEffect, useState } from "react";
import { getMirrorJobs, mirrorStatus as postMirrorStatus } from "../services/api";

/* the REAL production Job Status stages (monolith ABX_STAGES, L5846) */
const ABX_STAGES = [
  "Email Request Submitted",
  "Creative Traffic & Review",
  "Needs More Info from Division",
  "Division Review Email",
  "Email Approved to Send",
  "Email Scheduled",
] as const;
type Stage = (typeof ABX_STAGES)[number];

/* monolith abxStage(rs) — internal rs values → the real stages */
const RS_TO_STAGE: Record<string, Stage> = {
  "Submitted": "Email Request Submitted",
  "Waiting for Creative Review": "Creative Traffic & Review",
  "In Review": "Creative Traffic & Review",
  "In Creative Review": "Creative Traffic & Review",
  "Changes Requested": "Needs More Info from Division",
  "Locked – Pending Approval": "Division Review Email",
  "Division Approved": "Email Approved to Send",
  "Scheduled / Sent": "Email Scheduled",
};
/* monolith ABX_RS_FROM_REAL — mirror Status → internal rs */
const RS_FROM_REAL: Record<string, string> = {
  "Email Request Submitted": "Submitted",
  "Creative Traffic & Review": "In Creative Review",
  "Needs More Info from Division": "Changes Requested",
  "Division Review Email": "Locked – Pending Approval",
  "Email Approved to Send": "Division Approved",
  "Email Scheduled": "Scheduled / Sent",
  "Complete": "Scheduled / Sent",
};
export function abxStage(rs?: string): Stage {
  if (!rs) return "Email Request Submitted";
  if (RS_TO_STAGE[rs]) return RS_TO_STAGE[rs];
  return (ABX_STAGES as readonly string[]).indexOf(rs) > -1 ? (rs as Stage) : "Email Request Submitted";
}

interface Job {
  mirrorRec?: string;
  project?: string;
  div?: string;
  rt?: string;
  rs?: string;
  jobStatus?: string;
  subject?: string;
  pre?: string;
  deploy?: string;
  due?: string;
  rounds?: number;
  sfmcAsset?: string | null;
  lockedBy?: string | null;
  emailName?: string;
  previewUrl?: string;
  tpl?: string;
  submittedBy?: string;
  rmt?: boolean;
}

const TABS: [string, string][] = [
  ["board", "Review Board"], ["review", "Review Queue"], ["backend", "Back-end detail"],
  ["sfmcsync", "Pipeline"], ["audiences", "Audiences & DEs"], ["diag", "Diagnostics"],
];

const FLOW: [string, string, string][] = [
  ["📝", "Division submits", "form + Design Email build"],
  ["📦", "Job arrives BUILT", "real job row · SFMC asset · preview link · real email name"],
  ["🎨", "Creative revises", "full Studio · Save revision v2, v3…"],
  ["🔒", "Gate + lock", "brand/legal checks · frozen for division"],
  ["🏠", "Division approves", "signs off on the final version"],
  ["📅", "Scheduled", "only creative can press send"],
];

export default function BoardView({ division }: { division: string }) {
  const [tab, setTab] = useState("board");
  const [jobs, setJobs] = useState<Job[]>([]);
  const [sel, setSel] = useState<Job | null>(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  /* /mirror-jobs → board rows (monolith admMirrorLoad row mapping) */
  const load = useCallback(() => {
    getMirrorJobs()
      .then((d) => {
        const rows = (d && d.jobs) || [];
        setJobs(rows.map((r) => {
          const g = (k: string) => (r[k] == null ? "" : String(r[k]));
          return {
            mirrorRec: g("id"),
            project: g("project") || "Untitled",
            div: g("div") || "—",
            rt: g("requestType") || "—",
            rs: RS_FROM_REAL[g("status")] || "Submitted",
            jobStatus: g("status"),
            subject: g("subject") || "—",
            pre: g("preheader") || "—",
            deploy: g("deploy") || "",
            due: g("due") || "—",
            rounds: Number(r["round"] || 1),
            sfmcAsset: g("assetId") || null,
            lockedBy: g("lockedBy") || null,
            emailName: g("emailName"),
            previewUrl: g("previewUrl"),
            tpl: g("tpl"),
            submittedBy: g("submittedBy"),
            rmt: /rmt pilot/i.test(g("submittedBy")),
          } as Job;
        }));
      })
      .catch((e) => setErr(String(e)));
  }, []);
  useEffect(load, [load]);

  async function mirrorStatus(j: Job, jobStatus: string, decision?: string) {
    setBusy(true);
    try {
      await postMirrorStatus({ recordId: j.mirrorRec, assetId: j.sfmcAsset || undefined, jobStatus, decision, notes: note || undefined });
      load();
      setSel(null);
      setNote("");
    } catch (e) {
      setErr(String(e));
    } finally {
      setBusy(false);
    }
  }

  const buckets = {} as Record<Stage, Job[]>;
  ABX_STAGES.forEach((s) => { buckets[s] = []; });
  jobs.forEach((j) => buckets[abxStage(j.rs)].push(j));

  return (
    <div className="adm">
      {/* tab bar (monolith #admin .adm-tabs, L1294 — MVP switch removed in the
          MVP-only cutover) */}
      <div className="adm-tabs">
        {TABS.map(([k, l]) => (
          <button key={k} type="button" className={tab === k ? "on" : ""} onClick={() => setTab(k)}>{l}</button>
        ))}
      </div>

      {tab === "board" ? (
        <>
          <div className="at-hd">
            <div><span className="at-eye">SFMC Sandbox · Projects (Mirror)</span><h3>Creative Review Board</h3></div>
            <span className="sp" />
            <a className="at-open" href="https://airtable.com/appzOFSAIzFpovvIl/tblfzhHRrdWeJeYK6" target="_blank" rel="noopener noreferrer">Open in Airtable ↗</a>
          </div>
          <div className="abx-kpis">
            {ABX_STAGES.map((s, si) => (
              <div key={s} className={"abx-kpi s" + (si + 1)}><div className="n">{buckets[s].length}</div><div className="l">{s}</div></div>
            ))}
          </div>
          <div className="abx-take"><b>Division proposes, creative disposes.</b> Click a job to review it, revise it in the Studio, run the brand/legal gate, lock it, and gate the send.</div>
          <div className="abx-flow">
            {FLOW.map(([ico, b, s], i) => (
              <span key={b} style={{ display: "contents" }}>
                <div className="abx-fstep"><span className="ico">{ico}</span><b>{b}</b><span>{s}</span></div>
                {i < FLOW.length - 1 && <span className="abx-farrow">→</span>}
              </span>
            ))}
            <div className="abx-fnote">Same job format as the production pipeline — the only additions: <b>Source = Email Creative Hub</b> + <b>Studio Preview URL</b>. At cutover these rows write straight into the real Active Email Requests queue.</div>
          </div>
          {err && <div style={{ color: "#b23b3b", fontSize: 12, margin: "0 0 10px" }}>⚠ {err}</div>}
          <div className="abx-board">
            {ABX_STAGES.map((s) => (
              <div key={s} className="abx-col">
                <h3>{s}<span className="ct">{buckets[s].length}</span></h3>
                {buckets[s].length === 0 && <div className="abx-empty">—</div>}
                {buckets[s].map((j, i) => (
                  <div key={(j.mirrorRec || "") + i} className="abx-card" onClick={() => setSel(j)}>
                    <span className="dv">{j.div || "—"}</span>
                    {j.rmt && <span className="rmt-tag" title="Submitted via the RMT pilot">RMT</span>}
                    <div className="pj">{j.project || "Untitled"}</div>
                    <div className="meta">{j.rt || ""}{j.deploy ? " · deploy " + j.deploy : ""}</div>
                    {(j.rounds || j.sfmcAsset)
                      ? <div className="rev">{j.rounds ? "round " + j.rounds : ""}{j.sfmcAsset ? (j.rounds ? " · " : "") + "asset " + j.sfmcAsset : ""}{j.lockedBy ? " · 🔒 " + j.lockedBy : ""}</div>
                      : <div className="norev">no build yet</div>}
                  </div>
                ))}
              </div>
            ))}
          </div>
          <div className="adm-foot">Cards are the live admin jobs (submissions land here in real time). Click one for the full review modal — Revise in Studio, Save revision, gate, lock, approve, schedule.</div>
        </>
      ) : (
        <div className="adm-pending">
          This console tab isn't ported yet — it still lives in the production monolith. Review Board is fully live here.
        </div>
      )}

      {sel && (
        <div className="rb-ovl" onClick={(e) => e.target === e.currentTarget && setSel(null)}>
          <div className="rb-modal" style={{ width: "min(560px, 96vw)" }} onClick={(e) => e.stopPropagation()}>
            <div className="rb-modal-hd"><b>{sel.project || sel.subject}</b><button onClick={() => setSel(null)}>×</button></div>
            <div className="rb-modal-bd">
              <div className="adm-facts">
                <div><b>Division</b> {sel.div || division || "—"}</div>
                <div><b>Subject</b> {sel.subject || "—"}</div>
                <div><b>Deploy</b> {sel.deploy || "—"}</div>
                <div><b>Stage</b> {abxStage(sel.rs)}</div>
                {sel.emailName ? <div><b>Email name</b> <span style={{ fontFamily: "ui-monospace,Menlo,monospace", fontSize: 10.5 }}>{sel.emailName}</span></div> : null}
                {sel.previewUrl ? <div><b>Preview</b> <a href={sel.previewUrl} target="_blank" rel="noopener noreferrer">hosted preview ↗</a></div> : null}
                {!sel.mirrorRec && !sel.sfmcAsset && <div style={{ color: "#7d8291" }}>No mirror link on this job — stage actions need a mirror row (submits from this app carry it).</div>}
              </div>
              <label style={{ display: "block", margin: "12px 0" }}>
                <span style={{ display: "block", fontSize: 11, fontWeight: 800, color: "#7d8291", textTransform: "uppercase", letterSpacing: ".05em", marginBottom: 5 }}>Reviewer note (optional)</span>
                <textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} style={{ width: "100%", border: "1px solid #d3d9e2", borderRadius: 9, padding: "9px 11px", font: "inherit", fontSize: 13, boxSizing: "border-box" }} />
              </label>
              <div style={{ display: "flex", gap: 9, flexWrap: "wrap" }}>
                <button className="adm-act" disabled={busy} onClick={() => mirrorStatus(sel, "Changes Requested", "Changes Requested")}>Request changes</button>
                <button className="adm-act" disabled={busy} onClick={() => mirrorStatus(sel, "Division Review Email")}>→ Division Review Email</button>
                <button className="adm-act primary" disabled={busy} onClick={() => mirrorStatus(sel, "Email Approved to Send", "Approved")}>Email Approved to Send</button>
              </div>
              <div style={{ fontSize: 11, color: "#7d8291", marginTop: 10 }}>Creative gates Scheduled/Sent — there is NO send path anywhere in this tool (same rule as legacy).</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
